import { ArrowLeft, Save } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { toast } from 'sonner'
import { useUserRol, useTienePermiso } from '../App'
import { actualizarAceptado, guardarPrueba } from '../services/pruebasService'
import { RechazoModal } from "./RechazoModal"
import { Button } from "./ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Input } from "./ui/input"
import { Label } from "./ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select"

interface FormData {
  fecha: string
  hora: string
  grados: string
  hr: string
  no1_1: string
  no1_2: string
  no2_1: string
  no2_2: string
  no3_1: string
  no3_2: string
  no4_1: string
  no4_2: string
  no5_1: string
  no5_2: string
  realizo: string
  superviso: string
  promedio: string
  redondeo: string
  numero_solicitud?: string;
  id_termohigrometro?: string;
  desviacion_metodo?: string;
  factorCorreccion?: string;
}

interface NMXA3801FormProps {
  pedidoNumero: string
  pedidoRowId?: string
  onBack: () => void
  onSave: (data: FormData) => void
  autorizo: string
  initialData?: Partial<FormData>
  mode?: 'create' | 'view'
  rol?: string
  onRefreshBackend?: () => void
  onAutosave?: (result: { rowId: string; nomId: string }) => void
}

// Utility para truncar a 1 decimal sin redondear
function truncate1Decimal(num: number): string {
  const truncated = Math.trunc(num * 10) / 10;
  return truncated.toFixed(1);
}

export function NMXA3801Form({ pedidoNumero, pedidoRowId, onBack, onSave, autorizo, initialData = {}, mode = 'create', rol, onRefreshBackend, onAutosave }: NMXA3801FormProps) {
  const globalRol = typeof useUserRol === 'function' ? useUserRol() : undefined;
  const puedeRevisar = useTienePermiso('aprobar_rechazar_resultado');
  const effectiveRol = rol || globalRol;



  const [formData, setFormData] = useState<FormData>(() => {
    const baseData = {
      fecha: new Date().toISOString().split('T')[0],
      hora: new Date().toTimeString().split(' ')[0].substring(0, 5),
      grados: "",
      hr: "",
      no1_1: "",
      no1_2: "",
      no2_1: "",
      no2_2: "",
      no3_1: "",
      no3_2: "",
      no4_1: "",
      no4_2: "",
      no5_1: "",
      no5_2: "",
      realizo: "",
      superviso: "",
      promedio: "",
      redondeo: "",
      numero_solicitud: initialData?.numero_solicitud || "",
      id_termohigrometro: initialData?.id_termohigrometro || "",
      desviacion_metodo: initialData?.desviacion_metodo || "",
      factorCorreccion: initialData?.factorCorreccion || "100.12",
      ...initialData
    };

    // Set realizo field based on mode after spreading initialData
    if (mode === 'create') {
      baseData.realizo = autorizo || "";
    } else {
      // In view mode, try multiple possible keys from backend
      const realizoValue = initialData?.realizo || (initialData as any)?.REALIZO || "";
      baseData.realizo = realizoValue;
    }

    return baseData;
  })
  const [editMode, setEditMode] = useState(false)
  const [estatus, setEstatus] = useState<string>(initialData && (initialData as any).ACEPTADO ? (initialData as any).ACEPTADO : '')

  // Estado para modal de rechazo y loading
  const [showRechazoModal, setShowRechazoModal] = useState(false);
  const [rechazoLoading, setRechazoLoading] = useState(false);

  useEffect(() => {
    if (mode === 'create') {
      setFormData(prev => ({ ...prev, realizo: autorizo || "" }))
    }
  }, [autorizo, mode])

  useEffect(() => {
    if (initialData) {
      setFormData(prev => {
        const newData = {
          ...prev,
          ...initialData
        };

        // Handle realizo field based on mode
        if (mode === 'create') {
          newData.realizo = autorizo || "";
        } else {
          // In view mode, try multiple possible keys from backend
          const realizoValue = initialData.realizo || (initialData as any)?.REALIZO || "";
          newData.realizo = realizoValue;
        }

        return newData;
      });
      setEstatus((initialData as any)?.ACEPTADO || '');
      setEditMode(false);
    }
  }, [initialData, mode, autorizo])

  useEffect(() => {
    if (mode === 'view' && effectiveRol !== 'ADMIN') {
      setEditMode(false)
    }
  }, [effectiveRol, mode])

  useEffect(() => {
    if (mode === 'create' || editMode) {
      const valores = [
        parseFloat(formData.no1_2) || 0,
        parseFloat(formData.no2_2) || 0,
        parseFloat(formData.no3_2) || 0,
        parseFloat(formData.no4_2) || 0,
        parseFloat(formData.no5_2) || 0
      ].filter(val => val > 0)
      if (valores.length > 0) {
        const promedio = valores.reduce((sum, val) => sum + val, 0) / valores.length
        setFormData(prev => ({
          ...prev,
          promedio: truncate1Decimal(promedio),
          redondeo: Math.round(promedio).toString()
        }))
      } else {
        setFormData(prev => ({
          ...prev,
          promedio: "",
          redondeo: ""
        }))
      }
    }
  }, [formData.no1_2, formData.no2_2, formData.no3_2, formData.no4_2, formData.no5_2, mode, editMode])

  // Estado robusto para estatus (aceptado/rechazado)
  const initialDataSyncedRef = useRef(false)
  useEffect(() => {
    if (initialDataSyncedRef.current) return
    initialDataSyncedRef.current = true
    const raw = (initialData as any)?.ACEPTADO ?? (initialData as any)?.ACEPTAR ?? '';
    if (typeof raw === 'string') {
      const val = raw.toLowerCase().trim();
      if (val === 'aceptado' || val === 'rechazado') setEstatus(val);
      else setEstatus('');
    }
  }, [initialData]);

  // Robust estatus logic (like AnalisisQuimicoForm)
  const aceptadoRaw = (initialData as any)?.ACEPTADO ?? (initialData as any)?.aceptado ?? estatus;
  const aceptadoValue = typeof aceptadoRaw === 'string' ? aceptadoRaw.toLowerCase().trim() : '';
  const isAceptado = aceptadoValue === 'aceptado' || aceptadoValue === 'rechazado';
  const isReadOnly = mode === 'create' ? false : (mode === 'view' && !editMode);

  const handleInputChange = (field: keyof FormData, value: string) => {
    if (field === "factorCorreccion") {
      const factor = parseFloat(value) || 0;
      setFormData(prev => {
        const next: FormData = { ...prev, factorCorreccion: value };
        for (const n of [1, 2, 3, 4, 5]) {
          const pesoKey = `no${n}_1` as keyof FormData;
          const resKey = `no${n}_2` as keyof FormData;
          const peso = prev[pesoKey] as string;
          (next as any)[resKey] = peso && factor ? truncate1Decimal(parseFloat(peso) * factor) : "";
        }
        return next;
      });
    } else if (["no1_1", "no2_1", "no3_1", "no4_1", "no5_1"].includes(field)) {
      // Si cambia Peso (g), calcula Resultado automáticamente
      const num = field.charAt(2);
      const resultadoField = `no${num}_2` as keyof FormData;
      const factor = parseFloat(formData.factorCorreccion || "100.12");
      const resultado = value ? truncate1Decimal(parseFloat(value) * factor) : "";
      setFormData(prev => ({
        ...prev,
        [field]: value,
        [resultadoField]: resultado
      }));
    } else if (field === "redondeo") {
      // Redondeo solo se captura manualmente
      setFormData(prev => ({
        ...prev,
        redondeo: value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  }

  const formPayload = () => {
    // Convertir valores de resultado a números (manteniendo el truncamiento a 1 decimal)
    const processedData = { ...formData };
    ['no1_2', 'no2_2', 'no3_2', 'no4_2', 'no5_2'].forEach(field => {
      if (processedData[field as keyof FormData]) {
        const numValue = parseFloat(processedData[field as keyof FormData] as string);
        if (!isNaN(numValue)) {
          (processedData as any)[field] = numValue;
        }
      }
    });
    return processedData;
  }

  const [guardando, setGuardando] = useState(false)

  const handleSave = async () => {
    if (guardando) return
    setGuardando(true)
    try {
      // Cancela cualquier autoguardado pendiente y espera a que termine el que ya
      // esté en curso, para que el guardado explícito sea siempre el último en
      // escribir (si no, un autoguardado tardío puede revertir esto a "borrador").
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) {
        await new Promise(r => setTimeout(r, 100))
      }
      onSave(formPayload())
    } finally {
      setGuardando(false)
    }
  }

  // --- ADMIN ACTIONS ---
  const [autosaveRowId, setAutosaveRowId] = useState<string>((initialData as any)?.ROW_ID || (initialData as any)?.row_id || '');
  const [autosaveNomId, setAutosaveNomId] = useState<string>((initialData as any)?.nomId || '');
  const rowId = autosaveRowId;
  const nomId = autosaveNomId;
  const prueba = (formData as any)?.PRUEBA || (initialData as any)?.PRUEBA || 'NMX-A-3801-INNTEX-2012 PESO DE LA TELA (METODO 5)';

  // ── Autosave (borrador) ──────────────────────────────────────────────────
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isFirstAutosaveRenderRef = useRef(true)
  const autosaveInFlightRef = useRef(false)
  const rowIdRef = useRef(rowId)
  const nomIdRef = useRef(nomId)
  useEffect(() => { rowIdRef.current = rowId }, [rowId])
  useEffect(() => { nomIdRef.current = nomId }, [nomId])

  const doAutosave = async () => {
    if (autosaveInFlightRef.current) return
    autosaveInFlightRef.current = true
    try {
      const { pruebaId, nomId: savedNomId } = await guardarPrueba(
        'NMX-A-3801-INNTEX-2012 PESO DE LA TELA (METODO 5)',
        pedidoRowId || '',
        formPayload(),
        nomIdRef.current || undefined,
        rowIdRef.current || undefined,
        true,
      )
      setAutosaveRowId(String(pruebaId))
      setAutosaveNomId(savedNomId)
      rowIdRef.current = String(pruebaId)
      nomIdRef.current = savedNomId
      onAutosave?.({ rowId: String(pruebaId), nomId: savedNomId })
    } catch (err) {
      console.warn('Autosave de borrador falló (silencioso):', err)
    } finally {
      autosaveInFlightRef.current = false
    }
  }

  useEffect(() => {
    if (isFirstAutosaveRenderRef.current) {
      isFirstAutosaveRenderRef.current = false
      return
    }
    if (mode === 'view' || isAceptado) return

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null
      doAutosave()
    }, 1500)

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, mode, isAceptado])

  // Si hay un autoguardado pendiente (debounce de 1.5s) y el usuario navega hacia
  // atrás antes de que se dispare, se cancelaba sin guardarse nunca. Al salir,
  // se ejecuta de inmediato ese guardado pendiente con los datos más recientes.
  const handleBackFlush = () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
      autosaveTimerRef.current = null
      if (mode !== 'view' && !isAceptado) {
        doAutosave()
      }
    }
    onBack()
  }

  const handleAccion = async (accion: 'aceptado' | 'rechazado', razonRechazo?: string) => {
    const confirmMsg = accion === 'aceptado'
      ? `Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres aceptar la prueba ${prueba}?`
      : `Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres rechazar la prueba ${prueba}?`;
    if (!window.confirm(confirmMsg)) return;
    // Convertir valores de resultado a números (manteniendo el truncamiento a 1 decimal)
    const processedData = { ...formData };
    ['no1_2', 'no2_2', 'no3_2', 'no4_2', 'no5_2'].forEach(field => {
      if (processedData[field as keyof FormData]) {
        const numValue = parseFloat(processedData[field as keyof FormData] as string);
        if (!isNaN(numValue)) {
          (processedData as any)[field] = numValue;
        }
      }
    });
    const payload: any = {
      ...processedData,
      superviso: autorizo || formData.superviso || '',
      ROW_ID: rowId,
      PEDIDO: pedidoNumero,
      PRUEBA: prueba,
      ACEPTAR: accion,
      ACEPTADO: accion
    };
    if (accion === 'rechazado' && razonRechazo) {
      payload.RAZON_RECHAZO = razonRechazo;
    }
    try {
      if (accion === 'rechazado') setRechazoLoading(true);
      // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
      // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
      await actualizarAceptado(rowId, accion, accion === 'rechazado' ? payload.RAZON_RECHAZO : undefined, payload.superviso, nomId);
      setEstatus(payload.ACEPTAR)
      setEditMode(false)
      setShowRechazoModal(false)
      toast.success(`Prueba ${accion === 'aceptado' ? 'aceptada' : 'rechazada'} correctamente.`);
      if (typeof onRefreshBackend === 'function') setTimeout(() => onRefreshBackend(), 300);
    } catch (err) {
      alert('Error al enviar al backend: ' + (err as any)?.message)
    } finally {
      if (accion === 'rechazado') setRechazoLoading(false);
    }
  }

  const handleEditar = () => {
    setEditMode(true)
  }

  const handleGuardarEdicion = async () => {
    if (!window.confirm(`¿Seguro que quieres guardar y aceptar la edición de ${prueba}? Esta acción no se puede deshacer.`)) return;
    // Convertir valores de resultado a números (manteniendo el truncamiento a 1 decimal)
    const processedData = { ...formData };
    ['no1_2', 'no2_2', 'no3_2', 'no4_2', 'no5_2'].forEach(field => {
      if (processedData[field as keyof FormData]) {
        const numValue = parseFloat(processedData[field as keyof FormData] as string);
        if (!isNaN(numValue)) {
          (processedData as any)[field] = numValue;
        }
      }
    });
    const payload = {
      ...processedData,
      superviso: autorizo || formData.superviso || '',
      ROW_ID: rowId,
      PEDIDO: pedidoNumero,
      PRUEBA: prueba,
      ACEPTAR: 'aceptado',
      ACEPTADO: 'aceptado'
    };
    try {
      onSave({ ...payload, _EDIT_MODE: true } as any);
    } catch (err) {
      console.error('Error al guardar edición:', err);
    }
  }

  const renderRows = [1,2,3,4,5].map(num => (
    <tr key={num}>
      <td className="border border-border p-3">{num}</td>
      <td className="border border-border p-2">
        <Input
          type="number"
          step="0.001"
          placeholder="0.000"
          value={(formData as any)[`no${num}_1`]}
          onChange={(e) => handleInputChange(`no${num}_1` as keyof FormData, e.target.value)}
          className="text-center"
          disabled={isReadOnly}
        />
      </td>
      <td className="border border-border p-2">
        <Input
          type="number"
          step="0.1"
          placeholder="0.0"
          value={(formData as any)[`no${num}_2`] ? truncate1Decimal(parseFloat((formData as any)[`no${num}_2`])) : ""}
          disabled
          className="text-center bg-muted-foreground/10"
        />
      </td>
    </tr>
  ));

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={handleBackFlush}>
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Button>
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            NMX-A-3801-INNTEX-2012 
          </h1>
          <p className="text-muted-foreground">
            DETERMINACIÓN DE LA MASA DEL TEJIDO POR UNIDAD DE ÁREA (MÉTODO 5)
          </p>
          <p className="text-sm text-primary font-medium mt-1">Método: MET-7.2-8</p>
          <p className="text-muted-foreground">Pedido: {pedidoNumero}</p>
        </div>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Datos de Solicitud</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="numero_solicitud">Número de solicitud</Label>
              <Input
                id="numero_solicitud"
                type="text"
                placeholder="Ejemplo: 12345"
                value={formData.numero_solicitud || ""}
                onChange={e => handleInputChange('numero_solicitud', e.target.value)}
                disabled={isReadOnly}
              />
            </div>
            <div>
              <Label htmlFor="id_termohigrometro">ID de termohigrómetro</Label>
              <Select
                value={formData.id_termohigrometro || ""}
                onValueChange={value => handleInputChange('id_termohigrometro', value)}
                disabled={isReadOnly}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecciona" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="E-59">E-59</SelectItem>
                  <SelectItem value="E-57">E-57</SelectItem>
                  <SelectItem value="E-56">E-56</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Condiciones de Ensayo</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="fecha">FECHA</Label>
              <Input
                id="fecha"
                type="date"
                value={formData.fecha}
                onChange={(e) => handleInputChange('fecha', e.target.value)}
                disabled={isReadOnly}
              />
            </div>
            <div>
              <Label htmlFor="hora">HORA</Label>
              <Input
                id="hora"
                type="time"
                value={formData.hora}
                onChange={(e) => { if (e.target.value) handleInputChange('hora', e.target.value) }}
                disabled={isReadOnly}
              />
            </div>
            <div>
              <Label htmlFor="grados">GRADOS (°C)</Label>
              <Input
                id="grados"
                type="number"
                step="0.1"
                placeholder="20.0"
                value={formData.grados}
                onChange={(e) => handleInputChange('grados', e.target.value)}
                disabled={isReadOnly}
              />
            </div>
            <div>
              <Label htmlFor="hr">HR (%)</Label>
              <Input
                id="hr"
                type="number"
                step="0.1"
                placeholder="65.0"
                value={formData.hr}
                onChange={(e) => handleInputChange('hr', e.target.value)}
                disabled={isReadOnly}
              />
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Mediciones</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-border">
              <thead>
                <tr className="bg-muted">
                  <th className="border border-border p-3 text-left">Número de muestra</th>
                  <th className="border border-border p-3 text-center">Peso (g)</th>
                  <th className="border border-border p-3 text-center">Resultado (g/m²)</th>
                </tr>
              </thead>
              <tbody>
                {renderRows}
                <tr className="bg-accent">
                  <td className="border border-border p-3">PROMEDIO</td>
                  <td className="border border-border p-3 text-center">-</td>
                  <td className="border border-border p-3 text-center">
                    <span className="font-medium">{formData.promedio}</span>
                  </td>
                </tr>
                <tr className="bg-primary text-primary-foreground">
                  <td className="border border-border p-3">REDONDEO</td>
                  <td className="border border-border p-3 text-center">-</td>
                  <td className="border border-border p-3 text-center">
                    <Input
                      type="number"
                      step="1"
                      placeholder="Redondeo"
                      value={formData.redondeo}
                      disabled={true}
                      className="text-center text-black placeholder:black bg-background-gray-100"
                    />
                  </td>
                </tr>
                <tr>
                  <td className="border border-border p-3">DESVIACIÓN AL MÉTODO</td>
                  <td className="border border-border p-3 text-center" colSpan={2}>
                    <Input
                      type="text"
                      placeholder="Desviación al método"
                      value={formData.desviacion_metodo || ''}
                      onChange={e => handleInputChange('desviacion_metodo', e.target.value)}
                      disabled={isReadOnly}
                      className="text-center"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      {/* Factor de corrección */}
      <div className="rounded-md bg-muted-foreground/10 border border-border p-4 mb-2">
        <Label htmlFor="factorCorreccion" className="text-sm font-medium">Factor de corrección</Label>
        <Input
          id="factorCorreccion"
          type="number"
          step="0.01"
          placeholder="100.12"
          value={formData.factorCorreccion ?? "100.12"}
          onChange={e => handleInputChange('factorCorreccion', e.target.value)}
          readOnly={isReadOnly}
          className={`mt-1 w-40 ${isReadOnly ? "bg-muted-foreground/10 cursor-not-allowed" : "bg-background"}`}
        />
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Personal</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="realizo">REALIZÓ</Label>
              <Input
                id="realizo"
                placeholder="Nombre del técnico"
                value={formData.realizo}
                readOnly
                className="bg-muted-foreground/10 cursor-not-allowed"
              />
            </div>
            <div>
              <Label htmlFor="superviso">AUTORIZÓ</Label>
              <Input
                id="superviso"
                placeholder="Nombre del supervisor"
                value={formData.superviso}
                readOnly
                className="bg-muted-foreground/10 cursor-not-allowed"
              />
            </div>
          </div>
        </CardContent>
      </Card>
      <div className="flex justify-end gap-4">
        <Button variant="outline" onClick={handleBackFlush}>
          Cancelar
        </Button>
        {/* Botones de acciones en modo view y no aceptado SOLO PARA ADMIN */}
        {mode === 'view' && !editMode && estatus !== 'aceptado' && estatus !== 'rechazado' && puedeRevisar && (
          <>
            <Button variant="default" onClick={() => handleAccion('aceptado')}>Aceptar</Button>
            <Button variant="destructive" onClick={() => setShowRechazoModal(true)}>Rechazar</Button>
            <Button variant="secondary" onClick={handleEditar}>Editar</Button>
          </>
        )}
        {/* Botón guardar edición SOLO PARA ADMIN */}
        {mode === 'view' && editMode && estatus !== 'aceptado' && estatus !== 'rechazado' && puedeRevisar && (
          <Button variant="default" onClick={handleGuardarEdicion}>Guardar Edición</Button>
        )}
        {/* Botón guardar en modo create */}
        {mode === 'create' && (
          <Button onClick={handleSave} disabled={guardando}>
            <Save className="h-4 w-4 mr-2" />
            {guardando ? 'Guardando...' : 'Guardar Formulario'}
          </Button>
        )}
      </div>
      <RechazoModal
        open={showRechazoModal}
        onClose={() => setShowRechazoModal(false)}
        loading={rechazoLoading}
        onSubmit={(razon) => handleAccion('rechazado', razon)}
      />
    </div>
  )
}